import { Button } from "@/components/ui/button";
import { Download, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Navigation } from "@/components/Navigation";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SEO } from "@/components/SEO";
import { faqSchema } from "@/lib/structuredData";
import { pdfDownloadUrl } from "@/lib/pdfDownload";
import { trackCta, type CtaLocation } from "@/lib/trackCta";

const SevenSigns = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleDownload = (location: CtaLocation) => {
    trackCta(location, pdfDownloadUrl("7-signs", location), "Download 7 Signs PDF");

    const link = document.createElement("a");
    link.href = pdfDownloadUrl("7-signs", location);
    link.download = "7-signs-wake-up-guide.pdf";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download Started",
      description: "Your guide is downloading now.",
    });
  };

  const signs = [
    {
      number: 1,
      title: "You're Always on Autopilot",
      description: "You go through the motions of daily routines without really being present. Breakfast, work, dinner, bedtime—it all blurs together. You can't remember the details of your day or meaningful moments with your kids.",
      wakeUp: "Create \"presence anchors\" throughout your day. Set phone reminders to take three deep breaths and check in with yourself. Ask yourself: \"Am I here right now?\" Practice the 5-4-3-2-1 grounding technique: name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, and 1 you taste."
    },
    {
      number: 2,
      title: "Screens Are Your Escape Hatch",
      description: "The moment you have downtime, you reach for your phone, TV remote, or gaming controller. You're using screens not for entertainment but to avoid feeling anything—boredom, stress, or uncomfortable emotions.",
      wakeUp: "Implement \"tech-free zones\" in your home and schedule. Start with 30 minutes after work before touching your phone. Create a phone charging station outside of your bedroom."
    },
    {
      number: 3,
      title: "You Can't Remember the Last Time You Felt Joy",
      description: "Life feels flat and monotone. You're not necessarily depressed, but you're not really happy either. You exist in a gray zone where nothing excites you or moves you anymore, including things that used to bring you genuine pleasure.",
      wakeUp: "Start a \"joy journal.\" Each night, write down one moment from the day—no matter how small—that sparked anything positive. It might be your child's laugh, a good cup of coffee, or sunshine on your face. Train your brain to notice joy again. Schedule one activity weekly that used to bring you happiness before becoming a dad."
    },
    {
      number: 4,
      title: "Your Kids' Emotions Irritate You",
      description: "When your children express big feelings—crying, anger, excitement—your first instinct is to shut it down or leave the room. Their emotional needs feel like demands you can't meet. You find yourself saying \"stop crying\" or \"calm down\" more than offering comfort.",
      wakeUp: "Recognize that your discomfort with their emotions reflects your own emotional avoidance. Practice being present with one emotion at a time. Next time your child is upset, take a breath and simply stay. You don't need to fix it—just witness it. Say: \"I see you're feeling big feelings. I'm here.\" Consider talking to a therapist about your own emotional regulation."
    },
    {
      number: 5,
      title: "You're Always \"Too Tired\" for Connection",
      description: "Whenever your partner or kids want to talk, play, or spend quality time together, you claim exhaustion. While fatigue is real, you always seem to find energy for activities that help you zone out, but never for meaningful connection.",
      wakeUp: "Distinguish between physical tiredness and emotional avoidance. Commit to one daily connection moment that's non-negotiable—even just 10 minutes. Put it on your calendar like any other important meeting. Start small: play one game, have one real conversation, give one focused period of attention without distractions."
    },
    {
      number: 6,
      title: "You're Medicating with Substances or Behaviors",
      description: "You need that drink (or several) every evening to \"take the edge off.\" Or you're overdoing it with food, exercise, work, or shopping. These aren't occasional indulgences—they're your primary coping mechanism for getting through the day.",
      wakeUp: "Acknowledge what you're really trying to escape. Keep a log for one week: when do you reach for your numbing behavior, and what feeling preceded it? Develop alternative coping strategies like calling a friend, journaling, going for a walk, or doing breathwork. If your use feels compulsive, reach out to a counselor or support group."
    },
    {
      number: 7,
      title: "You Feel Like a Ghost in Your Own Life",
      description: "You're physically present but emotionally absent. Your family talks about memories you don't remember making. You feel disconnected from your own story, like you're watching your life happen to someone else rather than actively living it.",
      wakeUp: "Reconnect with your \"why.\" Write down what you want your kids to remember about you. What kind of dad do you want to be? Create a daily ritual that grounds you in your values—morning meditation, reading something meaningful, or stating an intention for the day. Talk to your partner or a trusted friend about what you're experiencing. Sometimes naming it breaks its power."
    }
  ];

  const faqs = signs.map(sign => ({
    question: `Sign ${sign.number}: ${sign.title}`,
    answer: `${sign.description} Wake Up Call: ${sign.wakeUp}`
  }));

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="7 Signs You're Numbing Out as a Dad | Rob Lasa"
        description="7 signs you might be living on autopilot—and some starting points for reconnecting with presence, purpose, and your kids."
        keywords="dad autopilot, numbing out, present father, emotional availability, dad wake up call, parenting presence, father self-awareness"
        type="article"
        url="/seven-signs"
        schema={faqSchema(faqs)}
      />
      <header className="border-b border-border/40 sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button onClick={() => handleDownload("seven_signs_page_header_download")} className="gap-2">
            <Download className="h-4 w-4" />
            Download PDF
          </Button>
        </div>
      </header>
      
      <Breadcrumbs />

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl md:text-6xl font-black mb-6 text-foreground">
            7 Signs You're Numbing Out as a Dad
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8">
            How to Wake Up Guide
          </p>
          <p className="text-lg text-foreground/80 mb-8 max-w-2xl mx-auto">
            You don't have to be perfect, Just be real. This guide will help you recognize the signs and take action to reconnect with what matters most.
          </p>
          <Button 
            onClick={() => handleDownload("seven_signs_page_hero_download")}
            size="lg" 
            className="text-lg px-8 py-6 gap-3"
          >
            <Download className="h-5 w-5" />
            Download Your Free Guide
          </Button>
        </div>
      </section>

      {/* Signs Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-12">
            {signs.map((sign) => (
              <article 
                key={sign.number}
                className="bg-card border border-border rounded-lg p-8 md:p-10 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-black">
                    {sign.number}
                  </div>
                  <h2 className="text-3xl font-bold text-foreground flex-1">
                    {sign.title}
                  </h2>
                </div>
                
                <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
                  {sign.description}
                </p>

                <div className="bg-accent/50 border-l-4 border-accent rounded-r-lg p-6">
                  <h3 className="text-xl font-bold text-accent-foreground mb-4 flex items-center gap-2">
                    🔥 Something to Try:
                  </h3>
                  <p className="text-foreground/90 leading-relaxed">
                    {sign.wakeUp}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-16 md:py-24 bg-secondary/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="bg-card border border-border rounded-lg p-8 md:p-10 shadow-sm">
            <h2 className="text-3xl md:text-4xl font-black text-foreground mb-6">
              The Path Forward
            </h2>
            <div className="space-y-4 text-lg text-foreground/80 leading-relaxed">
              <p>
                Recognizing that you've been numbing out isn't a sign of failure—it's the first step toward change. Many dads experience this, especially in a culture that doesn't encourage men to acknowledge their emotional needs.
              </p>
              <p>
                Waking up doesn't mean becoming perfect. It means choosing presence over avoidance, one moment at a time. Your kids don't need a perfect dad; they need a present one. And you deserve to actually experience your life, not just survive it.
              </p>
              <p>
                If you're struggling, reach out. Talk to a therapist, join a dad's group, or confide in a trusted friend. You don't have to do this alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary via-primary/90 to-secondary">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-black text-primary-foreground mb-6">
            Ready to Wake Up?
          </h2>
          <p className="text-xl text-primary-foreground/90 mb-8">
            Download the complete guide and start your journey to presence.
          </p>
          <Button 
            onClick={() => handleDownload("seven_signs_page_cta_download")}
            size="lg"
            variant="secondary"
            className="text-lg px-8 py-6 gap-3"
          >
            <Download className="h-5 w-5" />
            Download Your Free Guide
          </Button>
        </div>
      </section>

      <footer className="border-t border-border/40 py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">
            Rob Lasa — Raw conversations about fatherhood and identity.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SevenSigns;
